import type { CursorPresence } from "../types";

export type CursorPresenceMap = Record<string, CursorPresence>;

const hasValidCoordinates = (cursor: CursorPresence) =>
  typeof cursor.lat === "number" &&
  Number.isFinite(cursor.lat) &&
  typeof cursor.lon === "number" &&
  Number.isFinite(cursor.lon);

export const buildCursorPresenceMap = (
  entries: CursorPresence[]
): CursorPresenceMap => {
  const next: CursorPresenceMap = {};
  entries.forEach((entry) => {
    if (!entry?.userId) return;
    if (!entry.active || !hasValidCoordinates(entry)) return;
    next[entry.userId] = entry;
  });
  return next;
};

export const applyCursorPresenceUpdate = (
  current: CursorPresenceMap,
  update: CursorPresence
): CursorPresenceMap => {
  if (!update?.userId) return current;
  if (!update.active || !hasValidCoordinates(update)) {
    if (!current[update.userId]) {
      return current;
    }
    const next = { ...current };
    delete next[update.userId];
    return next;
  }
  return {
    ...current,
    [update.userId]: update
  };
};

export const filterCursorPresenceByRoster = (
  current: CursorPresenceMap,
  activeUserIds: Set<string>
): CursorPresenceMap => {
  let changed = false;
  const next: CursorPresenceMap = {};
  Object.entries(current).forEach(([userId, cursor]) => {
    if (activeUserIds.has(userId)) {
      next[userId] = cursor;
    } else {
      changed = true;
    }
  });
  return changed ? next : current;
};

export type ProjectedCursor = CursorPresence & {
  x: number;
  y: number;
};

export const projectCursorsToViewport = ({
  cursors,
  viewportWidth,
  viewportHeight,
  project
}: {
  cursors: CursorPresence[];
  viewportWidth: number;
  viewportHeight: number;
  project: (lat: number, lon: number) => { x: number; y: number };
}): ProjectedCursor[] =>
  cursors
    .filter((cursor) => cursor.active && hasValidCoordinates(cursor))
    .map((cursor) => {
      const point = project(cursor.lat as number, cursor.lon as number);
      return {
        ...cursor,
        x: point.x,
        y: point.y
      };
    })
    .filter(
      (cursor) =>
        cursor.x >= 0 &&
        cursor.y >= 0 &&
        cursor.x <= viewportWidth &&
        cursor.y <= viewportHeight
    );
